import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { notifyAlertCountsChanged, type WallMerge, type RoutingPoliciesApi, type EscalationsApi, type SilencesApi, type DeliveryStats } from '@trinetra/shared';
import { runWebrtcTeardown } from './webrtc-teardown';

const API_BASE = window.location.origin + '/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 35000, // Longer timeout for tunneled requests
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor: attach JWT token ──────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cloud_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * A message the operator can act on, whatever the node sent.
 *
 * WHY THIS EXISTS: a cloud request to a site travels Cloud BE → tunnel → that node's backend,
 * and the node's Express answers an unknown route with an HTML error page. Every layer then
 * dutifully forwards it, and the operator reads `Local service error: <!DOCTYPE html> <html
 * lang="en">…` in a toast — markup that says nothing about a situation with one obvious cause
 * and one obvious fix.
 *
 * A 404 from a node is almost never a missing record; it is a missing ROUTE, which means this
 * dashboard is newer than that site's build. That is normal in an estate where the cloud is
 * upgraded first, so it is named as such. Newer nodes report this themselves (the tunnel now
 * translates it), and this is the belt to that braces: the sites that most need the message are
 * exactly the ones too old to send it.
 */
function humanNodeError(message: string | undefined, status?: number): string {
  const text = String(message || '').trim();
  if (!/<!doctype|<html|<pre>/i.test(text)) return text || 'Request failed';

  // `[^\s<]+` rather than `\S+`: the path sits inside a <pre>, and a greedy match swallows the
  // closing tag into the endpoint name.
  const route = text.match(/Cannot (GET|POST|PUT|PATCH|DELETE) ([^\s<]+)/i);
  if (route) {
    return `This site's software does not have the ${route[1]} ${route[2]} endpoint yet — it is `
      + 'running an older build than this dashboard. Update the site to use this feature.';
  }
  if (status === 404) {
    return 'This site does not have that feature yet — it is running an older build than this '
      + 'dashboard.';
  }
  // Something else in markup: strip the tags rather than show them.
  const plain = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return plain.slice(0, 300) || 'The site returned an error with no readable message.';
}

// ─── Response interceptor: standardized error handling ──────────
// All backend responses follow: { success: boolean, message: string, data?: any }
// On error (4xx/5xx), we extract the message and reject with a clear error.
// On 401, we auto-logout and redirect to login.
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Backend returns { success, message, data } — check success flag
    const body = response.data;
    if (body && body.success === false) {
      const err = new Error(humanNodeError(body.message, response.status)) as any;
      err.statusCode = response.status;
      err.response = response;
      return Promise.reject(err);
    }
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const body = error.response?.data as any;

    // ── Retry transient tunnel failures for IDEMPOTENT reads only ──────────────
    // The cloud↔local-node tunnel can briefly flap; a single GET (alerts/events/
    // monitoring) then surfaces as a 502/503 ("tunnel not connected") or a network
    // error. Retry those a couple times with backoff so the user doesn't see an
    // intermittent failure. NEVER retry writes (POST/PUT/PATCH/DELETE) — a re-send
    // could double-write — and don't retry timeouts (the node was reachable but slow;
    // a retry just stacks another 35s wait).
    const config = error.config as (typeof error.config & { __retryCount?: number }) | undefined;
    const method = (config?.method || 'get').toLowerCase();
    const isIdempotent = method === 'get' || method === 'head';
    const isTimeout =
      error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || /timeout/i.test(error.message || '');
    const isTransient = status === 502 || status === 503 || (!error.response && !isTimeout);
    const MAX_RETRIES = 2;
    if (config && isIdempotent && isTransient && status !== 401) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 400 * (config.__retryCount as number))); // 400ms, 800ms
        return api.request(config);
      }
    }

    // Auto-logout on 401 (token expired/invalid)
    if (status === 401) {
      // Gracefully close ULLFU sessions before the token is gone — same path as
      // manual logout, so a forced/expired session doesn't leak a live PC.
      runWebrtcTeardown();
      localStorage.removeItem('cloud_token');
      localStorage.removeItem('cloud_user');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(new Error(body?.message || 'Session expired. Please login again.'));
    }

    // Extract standardized error message from backend, translating a stale site's HTML 404 into
    // the one sentence that helps — see humanNodeError.
    const message = humanNodeError(body?.message, status) || error.message || 'Network error';
    const enhancedError = new Error(message === 'Request failed' ? (error.message || message) : message) as any;
    enhancedError.statusCode = status;
    enhancedError.response = error.response;
    return Promise.reject(enhancedError);
  }
);

/**
 * Build a query string from optional params, DROPPING the ones that are not set.
 *
 * `new URLSearchParams({ subject: undefined })` does not skip the key — it stringifies the value,
 * and the request goes out as `?subject=undefined`. The node then rejects it:
 *
 *   Unknown subject 'undefined' — expected 'person' or 'vehicle'
 *
 * That is what broke the cloud Watch List's Detections tab on its DEFAULT view: "All subjects"
 * means no subject filter, which the shared page expresses as `undefined`, and every request 400'd.
 * The same call from a node works, because axios's own `{ params }` drops undefined — so the bug
 * could only ever appear on the cloud, on the tunnel path, which is the harder one to notice.
 *
 * The quieter half is worse: `search=undefined` is a VALID search, for the literal text
 * "undefined". An endpoint taking that returns no rows and no error, which reads as "nothing
 * matched" rather than as a broken request.
 *
 * `null` goes too, for the same reason. An empty string is left alone: a caller that writes
 * `search: ''` has said something, and these APIs already use `|| undefined` where they mean
 * "omit it".
 */
function qs(params?: Record<string, any> | null): string {
  if (!params) return '';
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    q.set(k, String(v));
  }
  const out = q.toString();
  return out ? `?${out}` : '';
}

/**
 * Helper: unwrap double-wrapped tunnel proxy responses.
 * Tunnel proxy returns { success, data: { success, message, data: actualData } }
 * After axios resp.data.data we get { success, message, data: actualData } OR actualData directly.
 * This normalizes it to always return the innermost data.
 */
function unwrap(outer: any): any {
  if (outer && typeof outer === 'object' && 'success' in outer && 'data' in outer) {
    return outer.data;
  }
  return outer;
}

// ─── Auth (Cloud FE → Cloud BE direct) ────────────────────────

/** POST /auth/login — Cloud FE → Cloud BE (direct) */
// export async function login(email: string, password: string) {
//   const resp = await api.post('/auth/login', { email, password });
//   localStorage.setItem('cloud_token', resp.data.data.token);
//   localStorage.setItem('cloud_user', JSON.stringify(resp.data.data.user));
//   return resp.data.data;
// }

export async function login(email: string, password: string) {
  const resp = await api.post('/auth/login', { email, password });
  const data = resp.data.data;
  localStorage.setItem('cloud_token', data.token);
  localStorage.setItem('cloud_user', JSON.stringify({ ...data.user, permissions: data.permissions }));
  return data;
}


/** POST /auth/register — Cloud FE → Cloud BE (direct) */
// export async function register(email: string, password: string, name: string) {
//   const resp = await api.post('/auth/register', { email, password, name });
//   localStorage.setItem('cloud_token', resp.data.data.token);
//   localStorage.setItem('cloud_user', JSON.stringify(resp.data.data.user));
//   return resp.data.data;
// }

export async function register(email: string, password: string, name: string) {
  const resp = await api.post('/auth/register', { email, password, name });
  const data = resp.data.data;
  localStorage.setItem('cloud_token', data.token);
  localStorage.setItem('cloud_user', JSON.stringify({ ...data.user, permissions: data.permissions }));
  return data;
}

/** GET /auth/me — Cloud FE → Cloud BE (direct) */
export async function getMe() {
  const resp = await api.get('/auth/me');
  return resp.data.data;
}

/**
 * Set (or clear, with null) MY favourite site — the scope a fresh session opens on.
 * Self-service, so it needs no user-management permission.
 */
export async function setDefaultSite(nodeId: string | null) {
  const resp = await api.put('/auth/me/default-site', { node_id: nodeId });
  return resp.data.data;
}

/**
 * Save (or, with null, reset) MY dashboard card layout — one layout for the All Sites and the
 * per-site dashboards, kept on the account. Self-service, like the default site.
 */
export async function setMyDashboardLayout(layout: unknown[] | null) {
  const resp = await api.put('/auth/me/dashboard-layout', { layout });
  return resp.data.data;
}

/**
 * Record that I finished or skipped an onboarding tour, so it is not offered again.
 * Self-service like setDefaultSite. Returns the server's updated `tour_state`.
 */
export async function saveTourState(tourId: string, version: number, status: 'completed' | 'skipped') {
  const resp = await api.put('/auth/me/tour', { tour_id: tourId, version, status });
  return resp.data.data as { tour_state: Record<string, { version: number; status: string; at: string }> };
}

export async function logout() {
  // Close ULLFU sessions gracefully (bypasses the resume grace) BEFORE clearing
  // the token and reloading — otherwise the persistent warm connection would
  // outlive the session and linger for the full grace window.
  runWebrtcTeardown();
  // Revoke the token server-side so it can't be reused before it expires.
  // Best-effort: the token is still in localStorage here (removed below), so the
  // request interceptor attaches it; proceed with local logout regardless.
  try {
    await api.post('/auth/logout', {}, { timeout: 3000 });
  } catch { /* ignore — always complete the local logout */ }
  localStorage.removeItem('cloud_token');
  localStorage.removeItem('cloud_user');
  window.location.href = '/login';
}

export function getStoredUser() {
  const user = localStorage.getItem('cloud_user');
  return user ? JSON.parse(user) : null;
}

export function getStoredToken() {
  return localStorage.getItem('cloud_token');
}

// ─── Nodes (Cloud FE → Cloud BE direct) ───────────────────────

/** GET /nodes — Cloud FE → Cloud BE (direct) */
export async function getNodes() {
  const resp = await api.get('/nodes');
  return resp.data.data;
}

/** GET /nodes/:nodeId — Cloud FE → Cloud BE (direct) */
export async function getNode(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}`);
  return resp.data.data;
}

/** POST /nodes/register — Cloud FE → Cloud BE (direct) */
export async function registerNode(nodeId: string, name: string, authToken: string) {
  const resp = await api.post('/nodes/register', { nodeId, name, authToken });
  return resp.data.data;
}

/** PUT /nodes/:nodeId — Cloud FE → Cloud BE (direct) */
export async function updateNode(nodeId: string, data: { name: string; authToken?: string }) {
  const resp = await api.put(`/nodes/${nodeId}`, data);
  return resp.data.data;
}

/** DELETE /nodes/:nodeId — Cloud FE → Cloud BE (direct) */
export async function deleteNode(nodeId: string) {
  const resp = await api.delete(`/nodes/${nodeId}`);
  return resp.data.data;
}

/** POST /nodes/:nodeId/access — Cloud FE → Cloud BE (direct) */
export async function grantNodeAccess(nodeId: string, userId: string, permissions: string) {
  const resp = await api.post(`/nodes/${nodeId}/access`, { userId, permissions });
  return resp.data.data;
}

// ─── Node Proxy (Cloud FE → Cloud BE → Tunnel → Local BE) ────

/**
 * GET /nodes/:nodeId/cameras — Cloud FE → Cloud BE → Tunnel → Local BE
 *
 * `groupIds` filters on the NODE, recursively (a group's sub-groups count), which is what the
 * local dashboard's filter does. Omit it for the whole list — every existing caller does.
 */
export async function getNodeCameras(
  nodeId: string,
  opts?: { groupIds?: string[]; search?: string; tagIds?: string[]; ptz?: string },
) {
  const groupIds = (opts?.groupIds || []).filter(Boolean);
  const tagIds = (opts?.tagIds || []).filter(Boolean);
  const resp = await api.get(`/nodes/${nodeId}/cameras`, {
    params: {
      limit: 1000,
      ...(groupIds.length ? { group_id_recursive: groupIds.join(',') } : {}),
      ...(opts?.search ? { search: opts.search } : {}),
      // Camera tags: OR within a category, AND across — resolved on the node.
      ...(tagIds.length ? { tag_ids: tagIds.join(',') } : {}),
      ...(opts?.ptz ? { ptz: opts.ptz } : {}),
    },
  });
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/cameras/names — id + name only, for filter dropdowns and
 * id→name labelling. Needs only `cameras.list`, so modules like attendance can
 * use it without being granted cameras.view (camera details + live video).
 */
export async function getNodeCameraNames(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/names`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras/:cameraId/snapshot — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getCameraSnapshot(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/${cameraId}/snapshot`, {
    timeout: 20000,
  });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/cameras/discover — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function discoverCameras(nodeId: string) {
  const resp = await api.post(`/nodes/${nodeId}/cameras/discover`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/cameras — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function addCamera(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/cameras`, data);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function editCamera(nodeId: string, cameraId: string, data: any) {
  const resp = await api.put(`/nodes/${nodeId}/cameras/${cameraId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function deleteCamera(nodeId: string, cameraId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/cameras/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras/:cameraId/status — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getCameraStatus(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/${cameraId}/status`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras/:cameraId/ullfu-url — decrypted ULLFU RTSP URL, admin/super_admin only */
export async function getCameraUllfuUrl(nodeId: string, cameraId: string): Promise<string> {
  const resp = await api.get(`/nodes/${nodeId}/cameras/${cameraId}/ullfu-url`);
  return unwrap(resp.data.data).url;
}

// ─── Groups (Cloud FE → Cloud BE → Tunnel → Local BE) ────────

/** GET /nodes/:nodeId/groups — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeGroups(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/groups`);
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/groups/for-filter — flat group list scoped to the user's
 * accessible cameras on the node. No groups.view required; use for the camera
 * filter when the user can see cameras but not manage groups.
 */
export async function getNodeGroupsForFilter(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/groups/for-filter`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/groups/:groupId/cameras — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getGroupCameras(nodeId: string, groupId: string, recursive = true) {
  const resp = await api.get(`/nodes/${nodeId}/groups/${groupId}/cameras`, { params: { recursive } });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/groups — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function createGroup(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/groups`, data);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/groups/:groupId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function updateGroup(nodeId: string, groupId: string, data: any) {
  const resp = await api.put(`/nodes/${nodeId}/groups/${groupId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/groups/:groupId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function deleteGroup(nodeId: string, groupId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/groups/${groupId}`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/groups/:groupId/cameras — Add cameras to group */
export async function addCamerasToGroup(nodeId: string, groupId: string, cameraIds: string[]) {
  const resp = await api.post(`/nodes/${nodeId}/groups/${groupId}/cameras`, { camera_ids: cameraIds });
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/groups/:groupId/cameras/:cameraId — Remove camera from group */
export async function removeCameraFromGroup(nodeId: string, groupId: string, cameraId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/groups/${groupId}/cameras/${cameraId}`);
  return unwrap(resp.data.data);
}

// ─── Map View layouts (Cloud FE → Cloud BE → Tunnel → Local BE) ─
//
// Layouts live on the NODE beside the cameras they describe, so both dashboards read the
// same floor plans. `getNodeMapLayout` carries the plan image inline as base64 — the tunnel
// is a JSON channel with no binary route to proxy.

/** GET /nodes/:nodeId/map-layouts — layout metadata (no image bytes). */
export async function getNodeMapLayouts(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/map-layouts`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/map-layouts/:layoutId — layout + placements + inline image. */
export async function getNodeMapLayout(nodeId: string, layoutId: string) {
  const resp = await api.get(`/nodes/${nodeId}/map-layouts/${layoutId}`);
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/analytics/summary — the site dashboard's AI analytics row.
 *
 * One call for every figure: per-active-model counts, alert severities and presence. The window
 * ("today since local midnight") is decided on the NODE, so the site's own day is what the cards
 * report rather than the browser's or the cloud's.
 *
 * `surfaces` is the site's active model surfaces (shared useActiveModelSurfaces) — the node
 * queries only those, so a site running one detector costs one query set.
 */
export async function getNodeAiSummary(
  nodeId: string,
  surfaces: string[],
  opts: {
    alerts?: boolean;
    attendance?: boolean;
    watchlist?: boolean;
    /** The search table's totals (the Search Summary card). Off unless asked for. */
    search?: boolean;
    fresh?: boolean;
    /** The window the figures count over, decided on the node. Omitted = the site's today. */
    range?: 'today' | '24h' | '7d';
  } = {},
) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/summary`, {
    params: {
      surfaces: surfaces.join(','),
      ...(opts.alerts === false ? { alerts: 0 } : {}),
      ...(opts.attendance === false ? { attendance: 0 } : {}),
      // The watchlist block is two more queries on the node; the model-surfaces call has no
      // cards for it and says so rather than paying for it twice per poll.
      ...(opts.watchlist === false ? { watchlist: 0 } : {}),
      ...(opts.search ? { search: 1 } : {}),
      // Only the Refresh button sends this — the poll rides the node's cache.
      ...(opts.fresh ? { fresh: 1 } : {}),
      ...(opts.range && opts.range !== 'today' ? { range: opts.range } : {}),
    },
  });
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/analytics/occupancy-series — the Person Count report's chart.
 *
 * Per-camera occupancy over the window, bucketed on the NODE. `from`/`to` are epoch SECONDS;
 * omit them and the node answers for its OWN day, which is the right default when the operator
 * reading this may be in a different timezone from the site.
 */
export async function getNodeOccupancySeries(
  nodeId: string,
  params: { from?: number; to?: number; camera_ids?: string; bucket?: number } = {},
) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/occupancy-series`, {
    params: {
      ...(params.from != null ? { from: params.from } : {}),
      ...(params.to != null ? { to: params.to } : {}),
      ...(params.camera_ids ? { camera_ids: params.camera_ids } : {}),
      ...(params.bucket ? { bucket: params.bucket } : {}),
    },
  });
  return unwrap(resp.data.data);
}

// ─── Live Wall (per site) — Cloud FE → Cloud BE → Tunnel → Local BE ──────────
//
// The layouts live on the NODE, so this app's per-site Live Wall and that node's own local
// dashboard show one set of grids. Reads arrive with each slot's camera name already resolved
// from the node's cameras table and slots outside the caller's camera access already dropped.

export interface SiteLiveWallSlot {
  cameraId: string;
  cameraName: string;
  gridPosition: number;
}

export interface SiteLiveWallView {
  id: string;
  name: string;
  cameras: SiteLiveWallSlot[];
  grid_size: string;
  /** Cells combined into one window; absent from a node that predates the feature. */
  merges?: WallMerge[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export async function getNodeLiveWallViews(nodeId: string): Promise<SiteLiveWallView[]> {
  const resp = await api.get(`/nodes/${nodeId}/live-wall/views`);
  return unwrap(resp.data.data) || [];
}

/** `null` rather than a 404 when nobody has starred a layout on this node yet. */
export async function getNodeLiveWallDefault(nodeId: string): Promise<SiteLiveWallView | null> {
  const resp = await api.get(`/nodes/${nodeId}/live-wall/views/default`);
  return unwrap(resp.data.data) || null;
}

export async function createNodeLiveWallView(
  nodeId: string,
  body: { name: string; cameras: SiteLiveWallSlot[]; gridSize: string; merges?: WallMerge[] },
): Promise<SiteLiveWallView> {
  const resp = await api.post(`/nodes/${nodeId}/live-wall/views`, body);
  return unwrap(resp.data.data);
}

export async function updateNodeLiveWallView(
  nodeId: string,
  viewId: string,
  body: { name?: string; cameras?: SiteLiveWallSlot[]; gridSize?: string; merges?: WallMerge[] },
): Promise<SiteLiveWallView> {
  const resp = await api.put(`/nodes/${nodeId}/live-wall/views/${viewId}`, body);
  return unwrap(resp.data.data);
}

export async function deleteNodeLiveWallView(nodeId: string, viewId: string) {
  await api.delete(`/nodes/${nodeId}/live-wall/views/${viewId}`);
}

export async function setNodeLiveWallDefault(nodeId: string, viewId: string): Promise<SiteLiveWallView> {
  const resp = await api.put(`/nodes/${nodeId}/live-wall/views/${viewId}/set-default`);
  return unwrap(resp.data.data);
}

export async function clearNodeLiveWallDefault(nodeId: string, viewId: string): Promise<SiteLiveWallView> {
  const resp = await api.delete(`/nodes/${nodeId}/live-wall/views/${viewId}/set-default`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/map-layouts/placed-cameras — cameraId → the layout holding it. */
export async function getNodeMapPlacedCameras(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/map-layouts/placed-cameras`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/map-layouts */
export async function createNodeMapLayout(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/map-layouts`, data);
  return unwrap(resp.data.data);
}

/** PATCH /nodes/:nodeId/map-layouts/:layoutId */
export async function updateNodeMapLayout(nodeId: string, layoutId: string, data: any) {
  const resp = await api.patch(`/nodes/${nodeId}/map-layouts/${layoutId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/map-layouts/:layoutId */
export async function deleteNodeMapLayout(nodeId: string, layoutId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/map-layouts/${layoutId}`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/map-layouts/:layoutId/placements — replaces the whole pin set. */
export async function setNodeMapPlacements(nodeId: string, layoutId: string, placements: any[]) {
  const resp = await api.put(`/nodes/${nodeId}/map-layouts/${layoutId}/placements`, { placements });
  return unwrap(resp.data.data);
}

// ─── Map View, geographic (Cloud FE → Cloud BE → Tunnel → Local BE) ─
//
// A camera's coordinates are columns on the camera row on the NODE, so this board and the
// node's own show one set of pins. Only coordinates travel; basemap tiles are fetched by the
// browser itself from the tile URL the node's config names.

/** GET /nodes/:nodeId/geo-map/config — the node's default view + basemap. */
export async function getNodeGeoMapConfig(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/geo-map/config`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/geo-map/config — save the default view. Needs `map.edit`. */
export async function setNodeGeoMapConfig(nodeId: string, body: any) {
  const resp = await api.put(`/nodes/${nodeId}/geo-map/config`, body);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/geo-map/placements — every camera that has coordinates. */
export async function getNodeGeoPlacements(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/geo-map/placements`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/geo-map/placements — a DELTA (`placed` / `cleared`). */
export async function setNodeGeoPlacements(nodeId: string, body: any) {
  const resp = await api.put(`/nodes/${nodeId}/geo-map/placements`, body);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras (paginated) — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeCamerasPaginated(nodeId: string, params: { page?: number; limit?: number; search?: string }) {
  // `search` was accepted here and then dropped, so the cloud's camera search only ever
  // searched the page already on screen. The cloud backend forwards it to the node.
  const resp = await api.get(`/nodes/${nodeId}/cameras`, {
    params: { limit: params.limit || 20, page: params.page || 1, ...(params.search ? { search: params.search } : {}) },
  });
  const outer = resp.data.data;
  // Need to preserve pagination info alongside data array
  if (outer && typeof outer === 'object' && 'success' in outer && 'data' in outer) {
    // Double-wrapped: outer = { success, message, data: [...], pagination: {...} }
    return { data: outer.data, pagination: outer.pagination, total: outer.pagination?.total };
  }
  return outer;
}

// ─── Discovery (Cloud FE → Cloud BE → Tunnel → Local BE) ─────

/** POST /nodes/:nodeId/discovery/scan — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function startDiscovery(nodeId: string, subnet?: string) {
  const resp = await api.post(`/nodes/${nodeId}/discovery/scan`, { subnet });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/discovery/results — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getDiscoveryResults(nodeId: string, params?: { page?: number; limit?: number; search?: string }) {
  const resp = await api.get(`/nodes/${nodeId}/discovery/results`, { params });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/discovery/bulk-approve — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function bulkApproveDiscovery(nodeId: string, cameras: any[]) {
  const resp = await api.post(`/nodes/${nodeId}/discovery/bulk-approve`, { cameras });
  return unwrap(resp.data.data);
}

// ─── Recordings (Cloud FE → Cloud BE → Tunnel → Local BE) ────

/** GET /nodes/:nodeId/recordings/dates — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordingDates(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/dates`, { params: { cameraId, tz_offset: new Date().getTimezoneOffset() } });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/timeline — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordingTimeline(nodeId: string, cameraId: string, date: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/timeline`, { params: { cameraId, date, tz_offset: new Date().getTimezoneOffset() } });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/segments — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordingSegments(nodeId: string, cameraId: string, startTime: string, endTime: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/segments`, { params: { cameraId, start_time: startTime, end_time: endTime } });
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/recordings/storage-by-camera — per-camera archive summary (size, segment
 * count, span) for the recordings picker. One tunnel round trip for every camera.
 */
export async function getRecordingStorageByCamera(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/storage-by-camera`);
  return unwrap(resp.data.data);
}

// ─── Recording mode configuration (continuous / event / motion) ───────────────
// Every call is Cloud FE → Cloud BE → Tunnel → Local BE /api/internal/recordings/config*,
// where the acting-user middleware re-applies the same capability checks as the local
// dashboard. A cloud operator can never exceed what that user could do locally.

/** GET /nodes/:nodeId/recordings/config — all configs + system defaults + form options */
export async function getRecordingConfigs(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/config`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/config/:cameraId */
export async function getRecordingConfig(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/config/${cameraId}`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/recordings/config/:cameraId */
export async function updateRecordingConfig(nodeId: string, cameraId: string, config: any) {
  const resp = await api.put(`/nodes/${nodeId}/recordings/config/${cameraId}`, config);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/recordings/config/bulk */
export async function bulkUpdateRecordingConfig(nodeId: string, cameraIds: string[], config: any) {
  const resp = await api.put(`/nodes/${nodeId}/recordings/config/bulk`, {
    camera_ids: cameraIds,
    config,
  });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/recordings/trigger/:cameraId — manual "record now" */
export async function triggerRecording(nodeId: string, cameraId: string, durationSeconds = 30) {
  const resp = await api.post(`/nodes/${nodeId}/recordings/trigger/${cameraId}`, {
    duration_seconds: durationSeconds,
  });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/motion-score/:cameraId — live meter (stream + ONVIF state) */
export async function getMotionScore(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/motion-score/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/motion-capability/:cameraId */
export async function getMotionCapability(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/motion-capability/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/triggers/:cameraId — retention-window audit log */
export async function getRecordingTriggers(
  nodeId: string,
  cameraId: string,
  from?: string,
  to?: string
) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/triggers/${cameraId}`, {
    params: { from, to },
  });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/live/start — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function startLive(nodeId: string, cameraId: string) {
  const resp = await api.post(`/nodes/${nodeId}/live/start`, { cameraId });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/live/stop — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function stopLive(nodeId: string, cameraId: string) {
  const resp = await api.post(`/nodes/${nodeId}/live/stop`, { cameraId });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordings(nodeId: string, params?: any) {
  const resp = await api.get(`/nodes/${nodeId}/recordings`, { params });
  return unwrap(resp.data.data);
}

// ─── Models (Cloud FE → Cloud BE → Tunnel → Local BE) ────────

/** GET /nodes/:nodeId/models — Cloud FE → Cloud BE → Tunnel → Local BE */
/**
 * GET /nodes/:nodeId/analytics/report-surfaces — report slugs with stored events on that site, so
 * the nav keeps a report visible after its model is unassigned.
 */
export async function getNodeReportSurfaces(nodeId: string): Promise<string[]> {
  const resp = await api.get(`/nodes/${nodeId}/analytics/report-surfaces`);
  // The cloud wraps the node's reply: resp.data.data is the node's own { success, data }, so it is
  // unwrapped like every other proxied read before `.surfaces` is reached.
  const body = unwrap(resp.data?.data);
  return (Array.isArray(body?.surfaces) ? body.surfaces : []) as string[];
}

export async function getNodeModels(nodeId: string, params?: any) {
  const resp = await api.get(`/nodes/${nodeId}/models`, { params });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/models — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function createNodeModel(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/models`, data);
  return unwrap(resp.data.data);
}

/** PATCH /nodes/:nodeId/models/:modelId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function updateNodeModel(nodeId: string, modelId: string, data: any) {
  const resp = await api.patch(`/nodes/${nodeId}/models/${modelId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/models/:modelId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function deleteNodeModel(nodeId: string, modelId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/models/${modelId}`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/models/:modelId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function attachModelToCamera(nodeId: string, modelId: string, cameraId: string, config?: any) {
  const resp = await api.post(`/nodes/${nodeId}/models/${modelId}/cameras/${cameraId}`, config || {});
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/models/:modelId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function detachModelFromCamera(nodeId: string, modelId: string, cameraId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/models/${modelId}/cameras/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras/:cameraId/models — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getCameraModels(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/${cameraId}/models`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/cameras/:cameraId/live-mode — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function setCameraLiveMode(nodeId: string, cameraId: string, mode: string) {
  const resp = await api.put(`/nodes/${nodeId}/cameras/${cameraId}/live-mode`, { mode });
  return unwrap(resp.data.data);
}

// ─── TURN (Cloud FE → Cloud BE direct) ───────────────────────

/** GET /turn/status — Cloud FE → Cloud BE (direct) */
export async function getTurnStatus() {
  const resp = await api.get('/turn/status');
  return unwrap(resp.data.data);
}

/** GET /turn/sessions — Cloud FE → Cloud BE (direct) */
export async function getTurnSessions() {
  const resp = await api.get('/turn/sessions');
  return unwrap(resp.data.data);
}

/** GET /turn/metrics — Cloud FE → Cloud BE (direct) */
export async function getTurnMetrics(minutes: number = 60) {
  const resp = await api.get('/turn/metrics', { params: { minutes } });
  return unwrap(resp.data.data);
}

/** GET /turn/connections — Cloud FE → Cloud BE (direct) */
export async function getTurnConnections() {
  const resp = await api.get('/turn/connections');
  return unwrap(resp.data.data);
}

// ─── Health (Cloud FE → Cloud BE direct) ──────────────────────

/** GET /health — Cloud FE → Cloud BE (direct) */
export async function getHealth() {
  const resp = await api.get('/health');
  return unwrap(resp.data.data);
}

// ─── Monitoring (Cloud FE → Cloud BE direct) ─────────────────

/** GET /monitoring/system — Cloud FE → Cloud BE (direct) */
export async function getMonitoringSystem() {
  const resp = await api.get('/monitoring/system');
  return unwrap(resp.data.data);
}

/** GET /monitoring/services — Cloud FE → Cloud BE (direct) */
export async function getMonitoringServices() {
  const resp = await api.get('/monitoring/services');
  return unwrap(resp.data.data);
}

/** GET /monitoring/sockets — Cloud FE → Cloud BE (direct) */
export async function getMonitoringSockets() {
  const resp = await api.get('/monitoring/sockets');
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes — Cloud FE → Cloud BE (direct) */
export async function getMonitoringLocalNodes() {
  const resp = await api.get('/monitoring/local-nodes');
  return unwrap(resp.data.data);
}

/** GET /monitoring/system/history — Cloud FE → Cloud BE (direct) */
export async function getMonitoringHistory(range: string = '1h') {
  const resp = await api.get(`/monitoring/system/history?range=${range}`);
  return unwrap(resp.data.data);
}

/** POST /monitoring/local-nodes/:nodeId/toggle-alerts — Cloud FE → Cloud BE (direct) */
export async function toggleNodeAlerts(nodeId: string) {
  const resp = await api.post(`/monitoring/local-nodes/${nodeId}/toggle-alerts`);
  return unwrap(resp.data.data);
}

// ─── Node Monitoring Proxy (Cloud FE → Cloud BE → Tunnel → Local BE) ─

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/system — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringSystem(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/system`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/system/history — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringHistory(nodeId: string, range: string = '1h', machineName?: string) {
  let url = `/monitoring/local-nodes/${nodeId}/proxy/monitoring/system/history?range=${range}`;
  if (machineName && machineName !== 'all') url += `&machine_name=${machineName}`;
  const resp = await api.get(url);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/cloud-tunnel — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringCloudTunnel(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/cloud-tunnel`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/recording-playback — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringRecPlayback(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/recording-playback`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/system/pipelines — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodePipelines(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/system/pipelines`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/sfu — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeUllfuSessions(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/sfu`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/uptime — Cloud FE → Cloud BE (direct, from DB) */
export async function getNodeUptimeLog(nodeId: string, limit: number = 100) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/uptime?limit=${limit}`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/machines — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringMachines(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/machines`);
  return unwrap(resp.data.data);
}

// ─── Analytics (Cloud FE → Cloud BE → Tunnel → Local Analytics Service) ─

/** GET /monitoring/local-nodes/:nodeId/analytics/events — Cloud FE → Cloud BE → Tunnel → Local Analytics */
export async function getNodeAnalyticsEvents(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events${query}`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/analytics/events/stats — Cloud FE → Cloud BE → Tunnel → Local Analytics */
export async function getNodeAnalyticsStats(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/stats`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/analytics/events/:eventId/images/:imageType — Cloud FE → Cloud BE → Tunnel → Local Analytics (binary) */
export async function getNodeAnalyticsEventImage(nodeId: string, eventId: string, imageType: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/${eventId}/images/${imageType}`, {
    responseType: 'blob',
  });
  return resp.data;
}

// ─── Alert Investigation (Cloud FE → Cloud BE → Tunnel → Local Analytics Service) ───

/** GET /monitoring/local-nodes/:nodeId/analytics/events/:model — model-specific events */
export async function getNodeModelEvents(nodeId: string, model: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/${model}${query}`);
  // Return full response (with pagination) — don't unwrap to array
  const inner = resp.data.data;
  return inner && typeof inner === 'object' && 'data' in inner ? inner : { data: inner, pagination: null };
}

/**
 * GET /monitoring/local-nodes/:nodeId/analytics/events/:model/:eventId — one event.
 *
 * `:eventId` resolves against either the AI `event_id` or the DB id (findEventRow in the
 * command-center), so a caller holding only the AI id — the attendance timeline's
 * `?event_id=` deep link — reaches the same row.
 */
export async function getNodeModelEvent(nodeId: string, model: string, eventId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/${model}/${encodeURIComponent(eventId)}`);
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/analytics/events/:model/stats — one model's aggregates over a window
 * (`from`/`to` epoch seconds), e.g. the dashboard's people-detected-by-camera list. The shape is
 * the command-center's `getStats` for that model, passed through untouched.
 */
export async function getNodeModelStats(nodeId: string, model: string, params?: Record<string, any>) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/events/${model}/stats`, { params });
  return unwrap(resp.data.data);
}

/**
 * ── ONE FRAME, ONE TRIP THROUGH THE TUNNEL ──
 *
 * Every snapshot on the cloud is a round trip Cloud FE → Cloud BE → WebSocket tunnel → the
 * site's command-center → back as base64. It is the slowest fetch this app makes, and the same
 * frame was being asked for again and again by parts of the page that do not know about each
 * other:
 *
 *   • a Search card streams the subject's crop, and opening that result streams EVERY frame of
 *     the event — the crop included, a second time, seconds later;
 *   • clicking the picture opens the lightbox, which streams it a third;
 *   • stepping ← → back to a record already read streams the whole event again;
 *   • the report table's row thumbnails and the record opened from that row are the same pair.
 *
 * None of it changes: a written snapshot is immutable, and the key names exactly one file. So a
 * frame is fetched once per session and held.
 *
 * WHAT IS CACHED IS THE BLOB, NOT AN OBJECT URL. Every caller already creates its own object URL
 * and revokes it on unmount, and handing out a shared URL would let the first component to close
 * revoke the picture out from under the two still showing it. Caching one layer below leaves
 * that lifecycle exactly as it was and removes only the network.
 *
 * The in-flight promise is cached too, which is the case the components above actually hit: the
 * gallery and the breakdown ask for the same crop in the same tick, and without this they open
 * two requests and only one of them was ever going to be needed.
 *
 * Bounded, because a long shift through a busy site would otherwise hold every frame ever looked
 * at. Plain insertion order — the oldest entries go first, which for a reader paging through
 * results is the records they have already left.
 */
const IMAGE_CACHE_MAX = 160;
const imageCache = new Map<string, Promise<any>>();

export async function getNodeModelEventImage(nodeId: string, model: string, eventId: string, imageType: string) {
  const key = `${nodeId}\u0000${model}\u0000${eventId}\u0000${imageType}`;
  const hit = imageCache.get(key);
  if (hit) {
    // Re-inserted so a frame the reader keeps coming back to is not the next one evicted.
    imageCache.delete(key);
    imageCache.set(key, hit);
    return hit;
  }

  const pending = api
    .get(`/monitoring/local-nodes/${nodeId}/analytics/events/${model}/${eventId}/images/${imageType}`, {
      responseType: 'blob',
    })
    .then((resp) => resp.data)
    .catch((err) => {
      // A failure is NOT cached. The frame may be missing because the site was briefly
      // unreachable, and a cached rejection would keep the picture broken for the rest of the
      // session even after the link came back.
      imageCache.delete(key);
      throw err;
    });

  imageCache.set(key, pending);
  if (imageCache.size > IMAGE_CACHE_MAX) {
    const oldest = imageCache.keys().next();
    if (!oldest.done) imageCache.delete(oldest.value);
  }
  return pending;
}

// ─── Alert config rules (Cloud FE → Cloud BE → Tunnel → Local command-center /api/config) ───

/**
 * The /nodes proxy, NOT the monitoring catch-all — same reason as `alertsWriteBase` below: the
 * generic monitoring write-proxies were removed in the RBAC pass, so every save, toggle,
 * duplicate and delete of a rule 404'd while the page still listed rules fine (the monitoring
 * GET proxy survived). These routes carry `alerts.config` and run the node-access check.
 *
 * Both the proxy and the tunnel pass the command-center's own `{ success, data }` body through
 * untouched, so responses come back double-wrapped — hence `unwrap` on each, since the page puts
 * the returned rule straight into its list.
 */
const cfgBase = (nodeId: string) => `/nodes/${nodeId}/analytics/config`;

/** List a node's alert-config rules. */
export async function getNodeAlertConfigs(nodeId: string) {
  const resp = await api.get(cfgBase(nodeId));
  const inner = unwrap(resp.data.data);
  return Array.isArray(inner) ? inner : [];
}

/** Create an alert-config rule on a node. */
export async function createNodeAlertConfig(nodeId: string, data: any) {
  const resp = await api.post(cfgBase(nodeId), data);
  return unwrap(resp.data.data);
}

/** Replace an alert-config rule. */
export async function updateNodeAlertConfig(nodeId: string, id: string, data: any) {
  const resp = await api.put(`${cfgBase(nodeId)}/${id}`, data);
  return unwrap(resp.data.data);
}

/** Toggle a rule active/inactive. */
export async function toggleNodeAlertConfig(nodeId: string, id: string) {
  const resp = await api.patch(`${cfgBase(nodeId)}/${id}/toggle`);
  return unwrap(resp.data.data);
}

/** Duplicate a rule. */
export async function duplicateNodeAlertConfig(nodeId: string, id: string) {
  const resp = await api.post(`${cfgBase(nodeId)}/${id}/duplicate`);
  return unwrap(resp.data.data);
}

/** Delete a rule. */
export async function deleteNodeAlertConfig(nodeId: string, id: string) {
  await api.delete(`${cfgBase(nodeId)}/${id}`);
}

/**
 * Every AI event kind the node has seen and its metadata fields — feeds the rule form's
 * pickers. Reaches the command-center's /api/config/event-catalog through the same proxy
 * route as a rule read.
 */
export async function getNodeEventCatalog(nodeId: string) {
  const resp = await api.get(`${cfgBase(nodeId)}/event-catalog`);
  const inner = unwrap(resp.data.data);
  return Array.isArray(inner) ? inner : [];
}

/** Partial edit of a rule (e.g. just its Alert Actions fields). */
export async function patchNodeAlertConfig(nodeId: string, id: string, data: any) {
  const resp = await api.patch(`${cfgBase(nodeId)}/${id}`, data);
  return unwrap(resp.data.data);
}

// ─── Cloud alerts — config + actions (Cloud FE → Cloud BE /api/alert-actions) ───
// Every alert the cloud sends: per site (AI, system, offline recipients), the cloud itself
// (platform recipients + its own CPU / memory / disk rules), the channels, the delivery log.

export async function getCentralAlertActions() {
  const resp = await api.get('/alert-actions');
  return resp.data.data;
}

export async function saveCentralAlertChannel(channel: string, body: any) {
  const resp = await api.put(`/alert-actions/channels/${channel}`, body);
  return resp.data.data;
}

export async function testCentralAlertChannel(channel: string, body: any) {
  const resp = await api.post(`/alert-actions/channels/${channel}/test`, body);
  return resp.data.data;
}

/** Start "Connect Google account" (Gmail API sender) — resolves with Google's consent URL. */
export async function connectCentralGmail(redirectUri: string) {
  const resp = await api.post('/alert-actions/gmail/connect', { redirect_uri: redirectUri });
  return resp.data.data;
}

export async function getCentralAlertLog(params: { nodeId?: string; status?: string; limit?: number; kind?: 'ai' | 'system' | 'offline' | 'cloud' } = {}) {
  const resp = await api.get('/alert-actions/log', { params });
  return resp.data.data || [];
}

/** The cloud's recipients for one site (AI analytics + system alerts). Cloud BE direct. */
export async function getCentralSiteAlertActions(nodeId: string) {
  const resp = await api.get(`/alert-actions/sites/${nodeId}`);
  return resp.data.data;
}

export async function saveCentralSiteAlertActions(nodeId: string, body: any) {
  const resp = await api.put(`/alert-actions/sites/${nodeId}`, body);
  return resp.data.data;
}

export async function testCentralSiteSystemAlert(nodeId: string, nodeName?: string) {
  const resp = await api.post(`/alert-actions/sites/${nodeId}/system-test`, { node_name: nodeName });
  return resp.data.data as { ok: boolean; message: string };
}

/** The users "also notify users with access to this site" reaches (granted the site; never admins). */
export async function getCentralSiteUsers(nodeId: string) {
  const resp = await api.get(`/alert-actions/sites/${nodeId}/users`);
  return resp.data.data || [];
}

/** Who hears about the cloud itself. */
export async function getPlatformAlertActions() {
  const resp = await api.get('/alert-actions/platform');
  return resp.data.data;
}

export async function savePlatformAlertActions(body: any) {
  const resp = await api.put('/alert-actions/platform', body);
  return resp.data.data;
}

export async function testPlatformAlert() {
  const resp = await api.post('/alert-actions/platform/test', {});
  return resp.data.data as { ok: boolean; message: string };
}

/** The cloud's own CPU / memory / disk rules. */
export async function getCloudSystemRules() {
  const resp = await api.get('/alert-actions/cloud-rules');
  return resp.data.data || [];
}

export async function saveCloudSystemRules(rules: any[]) {
  const resp = await api.put('/alert-actions/cloud-rules', { rules });
  return resp.data.data || [];
}

/** A SITE's own CPU / memory / disk rules — it evaluates them itself (through its tunnel). */
export async function getNodeSystemRules(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/alert-actions/system-rules`);
  return unwrap(resp.data.data) || [];
}

export async function saveNodeSystemRules(nodeId: string, rules: any[]) {
  const resp = await api.put(`/nodes/${nodeId}/alert-actions/system-rules`, { rules });
  return unwrap(resp.data.data) || [];
}

/** What a SITE sent itself, or handed to the cloud (through its tunnel). */
export async function getNodeAlertLog(nodeId: string, params: { kind?: string; limit?: number } = {}) {
  const resp = await api.get(`/nodes/${nodeId}/alert-actions/log`, { params });
  return unwrap(resp.data.data) || [];
}

/**
 * The SITE's own Alert Actions (through its tunnel): who sends its notifications, its
 * system-alert recipients and whether its channels are set up. alerts.config on both sides.
 */
export async function getNodeAlertActionsSite(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/alert-actions/site`);
  return unwrap(resp.data.data);
}

export async function saveNodeAlertActionsSite(nodeId: string, body: any) {
  const resp = await api.put(`/nodes/${nodeId}/alert-actions/site`, body);
  return unwrap(resp.data.data);
}

export async function testNodeSystemAlert(nodeId: string) {
  const resp = await api.post(`/nodes/${nodeId}/alert-actions/site/system-test`, {});
  return unwrap(resp.data.data) as { ok: boolean; message: string };
}

/** A site's Escalations tab: its metrics and system alerts (acknowledge / resolve), through its tunnel. */
export function nodeEscalationsApi(nodeId: string): EscalationsApi {
  return {
    metrics: async (days) => unwrap((await api.get(`/nodes/${nodeId}/alert-actions/escalations/metrics`, {
      params: { days, tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
    })).data.data),
    systemAlerts: async (status) => unwrap((await api.get(`/nodes/${nodeId}/system-alerts`, { params: { status } })).data.data) || [],
    setSystemAlertStatus: async (id, action, report) => unwrap((await api.post(`/nodes/${nodeId}/system-alerts/${id}/${action}`, report || {})).data.data),
  };
}

/** A site's pauses (maintenance windows) — on the site, through its tunnel. */
export function nodeSilencesApi(nodeId: string): SilencesApi {
  const base = `/nodes/${nodeId}/alert-actions/silences`;
  return {
    list: async () => unwrap((await api.get(base)).data.data) || [],
    save: async (id, body) => unwrap((id ? await api.put(`${base}/${id}`, body) : await api.post(base, body)).data.data),
    cancel: async (id) => unwrap((await api.post(`${base}/${id}/cancel`, {})).data.data),
    directory: async () => unwrap((await api.get(`/nodes/${nodeId}/alert-actions/policies/directory`)).data.data),
  };
}

/** A site's delivery report (its delivery log, summed up). */
export async function getNodeDeliveryStats(nodeId: string, days: number): Promise<DeliveryStats> {
  return unwrap((await api.get(`/nodes/${nodeId}/alert-actions/delivery/stats`, {
    params: { days, tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
  })).data.data);
}

/**
 * A site's routing policies (who gets which alert) — they live on the site, with its users and
 * cameras, so every call goes through its tunnel.
 */
export function nodeRoutingApi(nodeId: string): RoutingPoliciesApi {
  const base = `/nodes/${nodeId}/alert-actions/policies`;
  return {
    list: async (kind) => unwrap((await api.get(base, { params: { kind } })).data.data) || [],
    directory: async () => unwrap((await api.get(`${base}/directory`)).data.data),
    save: async (id, body) => unwrap((id ? await api.put(`${base}/${id}`, body) : await api.post(base, body)).data.data),
    remove: async (id) => unwrap((await api.delete(`${base}/${id}`)).data.data),
    preview: async (kind, sample) => unwrap((await api.post(`${base}/preview`, { kind, sample })).data.data),
    history: async (opts = {}) => unwrap((await api.get(`${base}/history`, { params: opts })).data.data) || [],
  };
}

// ─── Realtime Monitoring (Cloud FE → Cloud BE → Tunnel → Local BE /api/monitoring) ───

/** GET /monitoring/local-nodes/:nodeId/realtime-stats — hierarchy + alert counts */
export async function getNodeRealtimeStats(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/realtime-stats`);
  return unwrap(resp.data.data);
}

/**
 * GET /monitoring/local-nodes/:nodeId/demo-reset — does this site wipe itself on a timer?
 *
 * Drives the title-bar demo banner on pages that hold no alert-board socket. Shape:
 * { enabled, interval_minutes, next_reset_at, last_reset_at }.
 */
export async function getNodeDemoReset(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/demo-reset`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/realtime-alerts — latest alerts */
export async function getNodeRealtimeAlerts(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/realtime-alerts${query}`);
  return unwrap(resp.data.data);
}

// ─── Alert lifecycle (Cloud FE → Cloud BE → Tunnel → Local command-center /api/alerts) ───

const alertsBase = (nodeId: string) => `/monitoring/local-nodes/${nodeId}/analytics/alerts`;

/**
 * Writes go through the /nodes proxy, NOT the monitoring catch-all: the generic
 * POST/PATCH monitoring write-proxies were removed in the RBAC pass, so every
 * lifecycle call and comment on that path 404'd. These routes are gated on
 * `alerts.edit` and return the local body under `data`.
 */
const alertsWriteBase = (nodeId: string) => `/nodes/${nodeId}/analytics/alerts`;

/** List a node's alerts (full pagination object: { data, pagination }). */
export async function getNodeAlerts(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`${alertsBase(nodeId)}${query}`);
  // Cloud wraps the local payload under data; the local list returns { data, pagination }.
  return resp.data.data;
}

/**
 * The distinct alert NAMES a node has, with counts — the list's "Alert type" filter.
 *
 * Through the monitoring catch-all like the list itself, which forwards any `/analytics/*`
 * path, so this needed no new cloud route. The node derives these with the same expression it
 * filters by, which is what keeps a chosen name matchable.
 */
export async function getNodeAlertNames(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`${alertsBase(nodeId)}/names${query}`);
  const rows = resp.data?.data?.data ?? resp.data?.data ?? [];
  return Array.isArray(rows) ? rows : [];
}

/**
 * Aggregate alert counts for one node (`{ total, unacknowledged, by_severity, by_status, … }`).
 * Goes through the /nodes proxy — which has an explicit `/analytics/alerts/stats`
 * route ahead of `/analytics/alerts/:id` — rather than the monitoring catch-all,
 * so it stays gated on `alerts.view` instead of `monitoring.view`.
 */
export async function getNodeAlertStats(nodeId: string, params?: Record<string, any>) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/alerts/stats`, { params });
  const payload = resp.data?.data;
  return payload?.data ?? payload;
}

/**
 * Open AI-analytics alerts on one node, by severity — the title bar's AI-alert badge.
 *
 * One query on the node (see the CC controller), unlike `getNodeAlertStats`, which counts the
 * whole alerts table. A badge is polled by every open tab and has to answer "what still needs
 * attention", not "how many alerts have ever existed".
 */
export async function getNodeActiveAlertSummary(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/alerts/active-summary`);
  // CC answers { success, data: {...} }; the cloud proxy re-wraps under data.
  const payload = resp.data?.data;
  return payload?.data ?? payload;
}

/** A single node alert by id (for the alert-detail page). */
export async function getNodeAlert(nodeId: string, id: string) {
  const resp = await api.get(`${alertsBase(nodeId)}/${id}`);
  // CC get-by-id returns { success, data: <alert row> }; the cloud proxy re-wraps under data.
  const payload = resp.data?.data;
  return payload?.data ?? payload;
}

/** The node's users (local + provisioned cloud users with access), for the assign dropdown. */
export async function getNodeUsers(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/users`);
  const payload = resp.data?.data;
  return (Array.isArray(payload) ? payload : payload?.data) ?? [];
}

/**
 * Acknowledge a node alert.
 *
 * The lifecycle calls all announce themselves on alertCountsBus, which is what keeps the title
 * bar's badge from sitting on a stale number until its next poll — the one moment somebody
 * watches that number is right after they acted on an alert themselves. Announced here, in the
 * service, because the list, the board panel and the detail modal all act through these calls.
 */
export async function acknowledgeNodeAlert(nodeId: string, id: string, userId: string | null, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/acknowledge`, { user_id: userId, user_name: userName });
  notifyAlertCountsChanged();
  return unwrap(resp.data.data);
}
export async function unacknowledgeNodeAlert(nodeId: string, id: string, userId?: string | null, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/unacknowledge`, { user_id: userId, user_name: userName });
  notifyAlertCountsChanged();
  return unwrap(resp.data.data);
}
export async function assignNodeAlert(nodeId: string, id: string, assigneeId: string, userId: string | null, assigneeName?: string | null, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/assign`, {
    assignee_id: assigneeId,
    user_id: userId,
    assignee_name: assigneeName,
    user_name: userName,
  });
  notifyAlertCountsChanged();
  return unwrap(resp.data.data);
}
export async function resolveNodeAlert(nodeId: string, id: string, userId: string | null, note: string, userName?: string | null, cause?: string) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/resolve`, { user_id: userId, note, user_name: userName, ...(cause ? { cause } : {}) });
  notifyAlertCountsChanged();
  return unwrap(resp.data.data);
}
export async function cancelNodeAlert(nodeId: string, id: string, userId: string | null, reason: string, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/cancel`, { user_id: userId, reason, user_name: userName });
  notifyAlertCountsChanged();
  return unwrap(resp.data.data);
}
/**
 * Permanently delete a node alert. Gated on `alerts.delete` at the cloud proxy, then relayed
 * to the node's command-center as `DELETE /api/alerts/:id`. There is no undo — callers confirm.
 */
export async function deleteNodeAlert(nodeId: string, id: string) {
  const resp = await api.delete(`${alertsWriteBase(nodeId)}/${id}`);
  notifyAlertCountsChanged();
  return unwrap(resp.data?.data);
}
/** Per-alert action history: who assigned/acked/resolved/reopened/commented. */
export async function getNodeAlertActions(nodeId: string, id: string) {
  const resp = await api.get(`${alertsWriteBase(nodeId)}/${id}/actions`);
  return unwrap(resp.data.data);
}
/** Add a comment to a node alert. */
export async function addNodeAlertComment(nodeId: string, id: string, userId: string | null, userName: string | null, comment: string) {
  const resp = await api.post(`${alertsWriteBase(nodeId)}/${id}/comments`, { user_id: userId, user_name: userName, comment });
  return unwrap(resp.data.data);
}

// ─── Heatmap (Cloud FE → Cloud BE → Tunnel → Local Analytics Service) ───

/** GET /monitoring/local-nodes/:nodeId/analytics/heatmap/aggregated — aggregated heatmap grid */
export async function getHeatmapAggregated(nodeId: string, cameraId: string, from: string, to: string) {
  const params = new URLSearchParams({ camera_id: cameraId, from, to });
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/aggregated?${params}`);
  return unwrap(resp.data.data);
}

/**
 * GET /monitoring/local-nodes/:nodeId/analytics/heatmap/cameras — per-camera heatmap coverage
 * for the Heatmap report. One row per camera, no spatial grids; `from`/`to` are epoch ms.
 */
export async function getHeatmapCameraSummary(nodeId: string, params?: { from?: number; to?: number }) {
  // Through the shared builder like every other call here — it was hand-rolling the same thing,
  // and shadowing `qs` with a local while doing it.
  const query = qs({ from: params?.from, to: params?.to });
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/cameras${query}`);
  // Two envelopes: the cloud backend's, then the node's — hence `data.data` before `cameras`.
  return unwrap(resp.data.data)?.cameras || [];
}

/** GET /monitoring/local-nodes/:nodeId/analytics/heatmap/metrics — list heatmap metrics */
export async function getHeatmapMetrics(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/metrics${query}`);
  return resp.data;
}

/** Get the latest heatmap snapshot image as a blob URL */
export async function getHeatmapImage(nodeId: string, cameraId: string): Promise<string> {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/image/${cameraId}`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(resp.data);
}

/** Get URL for the latest heatmap snapshot image (returns empty string as placeholder — use getHeatmapImage instead) */
export function getHeatmapImageUrl(nodeId: string, cameraId: string): string {
  // This is a convenience for cases where you need a static URL
  // For actual usage, prefer getHeatmapImage() which handles auth
  return '';
}

// ─── Model Assignments ───────────────────────────────────────

/** GET /nodes/:nodeId/models/assignments/all */
export async function getNodeModelAssignments(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/models/assignments/all`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/models/assignments/bulk */
export async function bulkSaveNodeModelAssignments(nodeId: string, payload: any) {
  const resp = await api.put(`/nodes/${nodeId}/models/assignments/bulk`, payload);
  return unwrap(resp.data.data);
}

// ─── Face / Person Registration (Cloud FE → Cloud BE → Tunnel → Local Command-Center) ───
// Standalone faces live in the persons registry; employees live in the attendance module.
// Both hand vectors off to the external AI module over Redis (ai:faces:enroll) — the CC
// never stores embeddings. Status (vector_registered) is written back by the AI module.

/** GET /nodes/:nodeId/analytics/persons — list registered persons (the face registry). Returns the rows array. */
export async function getNodePersons(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/nodes/${nodeId}/analytics/persons${query}`);
  const rows = unwrap(resp.data.data);
  return Array.isArray(rows) ? rows : [];
}

/**
 * The same list, with the node's count of how many people match.
 *
 * The registry drawer pages and filters server-side, so the rows it holds are not the registry:
 * without `pagination.total` a truncated list looks exactly like a complete one. Separate from
 * `getNodePersons` because the other callers (attendance, search) want only the rows.
 */
export async function getNodePersonsPage(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/nodes/${nodeId}/analytics/persons${query}`);
  const outer = resp.data.data;
  // Double-wrapped: the cloud puts the node's whole body — rows AND pagination — under data.
  if (outer && typeof outer === 'object' && 'data' in outer) {
    const rows = Array.isArray(outer.data) ? outer.data : [];
    return { rows, total: Number(outer.pagination?.total ?? rows.length) };
  }
  const rows = Array.isArray(outer) ? outer : [];
  return { rows, total: rows.length };
}

/** POST /nodes/:nodeId/analytics/persons/from-event — register a NEW non-employee person from a face event */
export async function registerNodePersonFromEvent(nodeId: string, data: { event_id: string; name: string; label?: string; notes?: string }) {
  const resp = await api.post(`/nodes/${nodeId}/analytics/persons/from-event`, data);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/analytics/persons/:personId/link-from-event — attach a face event to an existing person */
export async function linkNodePersonFromEvent(nodeId: string, personId: string, data: { event_id: string }) {
  const resp = await api.post(`/nodes/${nodeId}/analytics/persons/${personId}/link-from-event`, data);
  return unwrap(resp.data.data);
}

/**
 * Files → the `{ base64, content_type }[]` the enrolment endpoints take.
 *
 * base64 rather than multipart because the node tunnel relays JSON; the node's controller
 * accepts either shape, so these are the same calls the local dashboard makes.
 */
async function filesToBase64(files: File[]) {
  return Promise.all(files.map((file) => new Promise<any>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    // readAsDataURL gives "data:image/jpeg;base64,xxxx" — the API wants only the payload.
    reader.onload = () => resolve({
      base64: String(reader.result).split(',')[1],
      content_type: file.type || 'image/jpeg',
    });
    reader.readAsDataURL(file);
  })));
}

/**
 * POST /nodes/:nodeId/analytics/persons/register — a NEW person from uploaded photos.
 *
 * The node mints the person_id: it is the key the AI module matches against for the life of
 * the identity, so it is not the browser's to choose.
 */
export async function registerNodePersonWithPhotos(
  nodeId: string,
  data: { name: string; label?: string; notes?: string },
  files: File[],
) {
  const images = await filesToBase64(files);
  const resp = await api.post(`/nodes/${nodeId}/analytics/persons/register`, { ...data, images });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/analytics/persons/:personId/photos — more enrollment photos. */
export async function uploadNodePersonPhotos(nodeId: string, personId: string, files: File[]) {
  const images = await filesToBase64(files);
  const resp = await api.post(`/nodes/${nodeId}/analytics/persons/${personId}/photos`, { images });
  return unwrap(resp.data.data);
}

// ── Watchlist ──
// Blacklisting is a property of the person, so it applies to employees too. `alert: false`
// flags them without raising alerts — see the node's setBlacklist.

export async function blacklistNodePerson(nodeId: string, personId: string, data: object = {}) {
  const resp = await api.put(`/nodes/${nodeId}/analytics/persons/${personId}/blacklist`, data);
  return unwrap(resp.data.data);
}

export async function unblacklistNodePerson(nodeId: string, personId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/analytics/persons/${personId}/blacklist`);
  return unwrap(resp.data.data);
}

// ── Watch-list sightings ──
// Who has actually been SEEN, people and vehicles in one time order. The node unions the two
// event tables; doing it here would mean interleaving two paginated feeds over the tunnel and
// getting the order wrong at every page boundary.

export async function getNodeWatchlistDetections(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/nodes/${nodeId}/analytics/watchlist/detections${query}`);
  const outer = resp.data.data;
  // Double-wrapped like the registry page above: rows AND pagination sit under data.
  if (outer && typeof outer === 'object' && 'data' in outer) {
    return { data: Array.isArray(outer.data) ? outer.data : [], pagination: outer.pagination };
  }
  return { data: Array.isArray(outer) ? outer : [], pagination: undefined };
}

export async function getNodeWatchlistSummary(nodeId: string, params?: Record<string, any>) {
  const query = qs(params);
  const resp = await api.get(`/nodes/${nodeId}/analytics/watchlist/detections/summary${query}`);
  return unwrap(resp.data.data) || { person: 0, vehicle: 0, total: 0 };
}

// ── Employment classification ──
// A person and an employee are one identity with an optional employment record, so these hang
// off the person rather than off /analytics/attendance/employees. Marking does NOT re-enroll:
// the AI module keys vectors by person_id and promotion does not change it.

/** POST /nodes/:nodeId/analytics/persons/:personId/employee — mark a registered person as staff */
export async function markNodePersonAsEmployee(nodeId: string, personId: string, data: object = {}) {
  const resp = await api.post(`/nodes/${nodeId}/analytics/persons/${personId}/employee`, data);
  return unwrap(resp.data.data);
}

/**
 * DELETE /nodes/:nodeId/analytics/persons/:personId/employee — end the employment, keep the person.
 *
 * Answers 409 with the counts when attendance history exists: deleting a month of timesheets
 * must be a decision, not a side effect. Re-send with `{ discard_attendance: true }`.
 */
export async function unmarkNodePersonAsEmployee(nodeId: string, personId: string, data: object = {}) {
  const resp = await api.delete(`/nodes/${nodeId}/analytics/persons/${personId}/employee`, { data });
  return unwrap(resp.data.data);
}

/**
 * DELETE /nodes/:nodeId/analytics/persons/:personId — delete a registered person
 * (vectors removed from AI module).
 *
 * Answers 409 when the person is also an EMPLOYEE, with the attendance counts: their
 * registry row is pinned by the employees foreign key, and going through takes the
 * employment record and its history with it. Re-send with `{ discard_attendance: true }`.
 */
export async function deleteNodePerson(nodeId: string, personId: string, data: object = {}) {
  const resp = await api.delete(`/nodes/${nodeId}/analytics/persons/${personId}`, { data });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/analytics/persons/:personId/faces — list a person's enrolled faces. Returns the rows array. */
export async function getNodePersonFaces(nodeId: string, personId: string) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/persons/${personId}/faces`);
  const rows = unwrap(resp.data.data);
  return Array.isArray(rows) ? rows : [];
}

/** DELETE /nodes/:nodeId/analytics/persons/:personId/faces/:photoId — unlink one face (AI module drops the vector) */
export async function unlinkNodePersonFace(nodeId: string, personId: string, photoId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/analytics/persons/${personId}/faces/${photoId}`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/analytics/attendance/employees/from-event — register a NEW employee from a face event */
export async function registerNodeEmployeeFromEvent(nodeId: string, data: { event_id: string; name: string; employee_code?: string }) {
  const resp = await api.post(`/nodes/${nodeId}/analytics/attendance/employees/from-event`, data);
  return unwrap(resp.data.data);
}

// ─── Structured event / attribute search ──────────────────────
//
// Cloud FE → Cloud BE → Tunnel → Local BE → Command Center.
//
// The hop through the local BACKEND (not straight to the node's command-center, as the other
// analytics reads go) is what gives these calls per-camera RBAC: only the backend holds the
// node's grants, and only the tunnel's backend client attaches X-Cloud-User-Id for it to
// resolve. The browser never names its own camera scope.

/** POST /nodes/:nodeId/search/events */
export async function searchNodeEvents(nodeId: string, dsl: any) {
  const resp = await api.post(`/nodes/${nodeId}/search/events`, dsl);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/search/facets */
export async function searchNodeFacets(nodeId: string, dsl: any) {
  const resp = await api.post(`/nodes/${nodeId}/search/facets`, dsl);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/search/refine — what the current results contain, counted. */
export async function searchNodeRefine(nodeId: string, dsl: any) {
  const resp = await api.post(`/nodes/${nodeId}/search/refine`, dsl);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/search/schema — the closed vocabularies the filter chips may offer. */
export async function getNodeSearchSchema(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/search/schema`);
  return unwrap(resp.data.data);
}

/**
 * ── Site settings (Cloud FE → Cloud BE → Tunnel → the node's own settings API) ──
 *
 * The selected site's settings, not the cloud's: everything here describes ONE node and is
 * answered by that node. The cloud has no copy of a site's storage limits, so there is nothing
 * to read while a site is offline — these calls fail with the tunnel's own 503, which the page
 * reports as "this site is not reachable" rather than as empty settings.
 */

/** GET /nodes/:nodeId/settings/storage — disk usage, per-camera breakdown. */
export async function getNodeStorage(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/settings/storage`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/settings/storage/config — retention and cap, with the node's .env defaults. */
export async function getNodeStorageConfig(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/settings/storage/config`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/settings/storage/config — needs settings.edit on BOTH sides. */
export async function setNodeStorageConfig(
  nodeId: string,
  // Every key is optional: the node writes only the fields it is sent, so the limits
  // and the recording pipeline options can be saved independently.
  body: {
    retention_days?: number | null;
    max_storage_gb?: number | null;
    realtime_playback_enabled?: boolean | null;
    separate_recording_pipeline?: boolean | null;
  },
) {
  const resp = await api.put(`/nodes/${nodeId}/settings/storage/config`, body);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/settings/recordings/config — engine defaults + per-camera modes. */
export async function getNodeRecordingConfig(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/settings/recordings/config`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/settings/license — status only; the .lic upload stays on the node. */
export async function getNodeLicense(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/settings/license`);
  return unwrap(resp.data.data);
}

/**
 * ── Camera tags (Cloud FE → Cloud BE → Tunnel → the node's /api/internal/camera-tags) ──
 *
 * The selected site's tag categories, tags, bulk tagging and PTZ detection. Tags belong to the
 * site, like its cameras; the cloud keeps no copy. Read needs cameras.view|list, every write
 * cameras.edit — on the cloud role and again on the node's provisioned user.
 */
export async function getNodeCameraTags(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/camera-tags`);
  return unwrap(resp.data.data);
}

export async function createNodeTagCategory(nodeId: string, body: { name: string; description?: string; multi_select?: boolean }) {
  const resp = await api.post(`/nodes/${nodeId}/camera-tags/categories`, body);
  return unwrap(resp.data.data);
}

export async function updateNodeTagCategory(nodeId: string, id: string, body: Record<string, unknown>) {
  const resp = await api.patch(`/nodes/${nodeId}/camera-tags/categories/${id}`, body);
  return unwrap(resp.data.data);
}

export async function deleteNodeTagCategory(nodeId: string, id: string) {
  const resp = await api.delete(`/nodes/${nodeId}/camera-tags/categories/${id}`);
  return unwrap(resp.data.data);
}

export async function createNodeTag(nodeId: string, categoryId: string, body: { name: string; color?: string | null }) {
  const resp = await api.post(`/nodes/${nodeId}/camera-tags/categories/${categoryId}/tags`, body);
  return unwrap(resp.data.data);
}

export async function updateNodeTag(nodeId: string, id: string, body: { name?: string; color?: string | null }) {
  const resp = await api.patch(`/nodes/${nodeId}/camera-tags/tags/${id}`, body);
  return unwrap(resp.data.data);
}

export async function deleteNodeTag(nodeId: string, id: string) {
  const resp = await api.delete(`/nodes/${nodeId}/camera-tags/tags/${id}`);
  return unwrap(resp.data.data);
}

/** Add/remove tags on many cameras of the site. */
export async function bulkAssignNodeTags(nodeId: string, cameraIds: string[], addTagIds: string[], removeTagIds: string[]) {
  const resp = await api.post(`/nodes/${nodeId}/camera-tags/assign`, {
    camera_ids: cameraIds, add_tag_ids: addTagIds, remove_tag_ids: removeTagIds,
  });
  return unwrap(resp.data.data);
}

export async function getNodePtzDetectStatus(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/camera-tags/capabilities/detect`);
  return unwrap(resp.data.data);
}

export async function startNodePtzDetect(nodeId: string, scope: 'unknown' | 'all') {
  const resp = await api.post(`/nodes/${nodeId}/camera-tags/capabilities/detect`, { scope });
  return unwrap(resp.data.data);
}

export { api };
export default api;
